import React, { useState, useEffect, useContext } from "react";
import Select from "react-select";
import "./CommercialOffer.css";
import { selectStyles } from "../../../../utils/selectStyles";
import VersionHistoryDrawer from "../Estimate/VersionHistoryDrawer/VersionHistoryDrawer";
import CommercialOfferApprovalModal from "./CommercialOfferApprovalModal";
import AddCounterpartyModal from "../../CounterpartiesDatabase/AddCounterpartyModal/AddCounterpartyModal";
import {
  getCommercialProposal,
  saveCpTitle,
  saveCpPaymentSchedule,
  requestCommercialProposalApproval,
  getCommercialProposalApprovals,
  cancelCommercialProposalApproval,
  getCpTeis,
  restoreVersion,
} from "../../../../api/projectEstimatesApi";
import { getAllCounterparties } from "../../../../api/api";
import { getProjectById } from "../../../../api/projectApi";
import { getAPIURL } from "../../../../api/config";
import { UserContext } from "../../../../contexts/CurrentUserContext";

// Подпись для контрагента в селектах
const getCounterpartyLabel = (c) =>
  c?.short_company_name || c?.full_name || c?.full_company_name || "";
const normalizeId = (id) => (id != null ? String(id) : null);
const getDirectorNameFromCounterparty = (counterparty) => {
  if (!counterparty) return "—";

  const managerName =
    counterparty.full_name_first_supervisor || counterparty.director_full_name;
  if (managerName) return managerName;

  // Для ИП в физлицах часто нет отдельного поля руководителя:
  // берем full_name и очищаем префикс "ИП" и кавычки.
  if (counterparty.type === "physical_entity" && counterparty.full_name) {
    return counterparty.full_name
      .replace(/^\s*ИП\s*/i, "")
      .replace(/[«»"]/g, "")
      .trim();
  }

  return "—";
};

const CREATE_DESIGNER_OPTION = { value: "__create_new_designer__", label: "+ Создать компанию" };
const CREATE_RECIPIENT_OPTION = { value: "__create_new_recipient__", label: "+ Создать контрагента" };

const CommercialOffer = ({ projectId, projectLockedByApproval = false }) => {
  const { userData } = useContext(UserContext);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [commercialData, setCommercialData] = useState(null);
  const [designersCounterparties, setDesignersCounterparties] = useState([]); // our_company — генпроектировщики
  const [recipientsCounterparties, setRecipientsCounterparties] = useState([]); // counterparty — получатели
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [isCancellingApproval, setIsCancellingApproval] = useState(false);

  // Состояния для редактирования полей
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingPaymentSchedule, setIsEditingPaymentSchedule] =
    useState(false);
  const [tempProjectName, setTempProjectName] = useState("");
  const [tempTitle, setTempTitle] = useState("");
  const [tempPaymentSchedule, setTempPaymentSchedule] = useState([]);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isSavingPaymentSchedule, setIsSavingPaymentSchedule] = useState(false);
  const [calculatingFields, setCalculatingFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoErrors, setLogoErrors] = useState(new Set());
  const [loadingLogos, setLoadingLogos] = useState(new Set());
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isEditable, setIsEditable] = useState(true); // Флаг редактируемости коммерческого предложения
  const [cpApprovalStatus, setCpApprovalStatus] = useState(null); // Статус согласования КП
  const [isCreatingCopy, setIsCreatingCopy] = useState(false); // Флаг создания копии
  const [showAddDesignerModal, setShowAddDesignerModal] = useState(false);
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);

  // Редактируемые блоки доп. информации (от «Разделы проектирования РП» до реквизитов) и подписи
  const defaultDesignSections = [
    "Разработка в стандартном варианте (2D) - ПОС, ОВОС, паспорт объекта, энергетический паспорт, ГП, ТХ, АПТ, сметная документация.",
    "Разработка с учетом BIM технологии - АС, КЖ, ВК, ОТ, ВиК, ЭМ, СС, ПС.",
  ];
  const defaultExcludedCosts = [
    "Инженерно-геологические и инженерно-геодезические изыскания.",
    "Экспертизу рабочего проекта.",
    "Разработку дизайна интерьера и ландшафтного дизайна.",
  ];
  const [designSectionsTitle, setDesignSectionsTitle] = useState(
    "Разделы проектирования РП:",
  );
  const [designSectionsItems, setDesignSectionsItems] = useState(
    defaultDesignSections,
  );
  const [excludedCostsTitle, setExcludedCostsTitle] = useState(
    "В настоящем ценовом предложении не включены затраты на:",
  );
  const [excludedCostsItems, setExcludedCostsItems] =
    useState(defaultExcludedCosts);
  const [deadlinesText, setDeadlinesText] = useState(
    "Сроки согласно утвержденному графику проектирования.",
  );
  const [performerName, setPerformerName] = useState("Казиев Б.М.");
  const [signatureDate, setSignatureDate] = useState("07.04.2025");
  const [isSavingAdditionalInfo, setIsSavingAdditionalInfo] = useState(false);
  const [isEditingAdditionalInfo, setIsEditingAdditionalInfo] = useState(false);

  // При активном согласовании сметы или КП блокируем редактирование (флаг с родительской страницы)
  const effectiveIsEditable = isEditable && !projectLockedByApproval;

  // Вспомогательная функция для определения редактируемости на основе версий и статусов
  const determineEditable = (commercialData, approvals, teisData) => {
    // Получаем текущую версию из данных КП
    const currentVersion = commercialData?.version;

    // Получаем максимальную версию из списка согласований
    const maxApprovalVersion =
      approvals && approvals.length > 0
        ? Math.max(
            ...approvals
              .map((a) => a.version)
              .filter((v) => v !== null && v !== undefined),
          )
        : null;

    // Сравниваем версии: если текущая версия больше максимальной из списка согласований,
    // то можно не блокировать редактирование
    const isVersionNewer =
      currentVersion !== null &&
      currentVersion !== undefined &&
      maxApprovalVersion !== null &&
      currentVersion > maxApprovalVersion;

    // Устанавливаем статус согласования КП из ТЭП
    if (teisData) {
      // Блокируем редактирование если статус "согласован", но не блокируем если версия новее
      const isApproved = teisData.cp_approval_status === "approved";
      return (
        commercialData.editable !== false && (!isApproved || isVersionNewer)
      );
    } else {
      // Устанавливаем флаг редактируемости из API
      // Если версия новее максимальной из согласований, разрешаем редактирование
      return commercialData.editable !== false || isVersionNewer;
    }
  };

  const enrichCommercialDataWithTeis = (commercial, teisData) => {
    if (!commercial || !Array.isArray(commercial.project_documentation)) {
      return commercial;
    }

    const teisBlocks = Array.isArray(teisData?.blocks) ? teisData.blocks : [];
    if (teisBlocks.length === 0) return commercial;

    const teisByBlockId = new Map(
      teisBlocks
        .filter((b) => b?.block?.id != null)
        .map((b) => [String(b.block.id), b]),
    );

    return {
      ...commercial,
      project_documentation: commercial.project_documentation.map((block) => {
        const blockId = block?.block?.id;
        const teisBlock =
          blockId != null ? teisByBlockId.get(String(blockId)) : null;
        return {
          ...block,
          attachment_coeff:
            teisBlock?.attachment_coeff ?? block?.attachment_coeff ?? 0,
        };
      }),
    };
  };

  // Нормализация графика платежей на основе итоговой суммы
  const normalizePaymentSchedule = (schedule, totalAmount) => {
    if (!schedule || schedule.length === 0) return schedule;
    if (!totalAmount || totalAmount === 0) return schedule;

    // Вычисляем текущую сумму всех платежей
    const currentTotal = schedule.reduce(
      (sum, payment) => sum + (payment.price || 0),
      0,
    );

    // Если сумма не совпадает с итоговой, нормализуем
    if (Math.abs(currentTotal - totalAmount) > 1) {
      // Пересчитываем проценты на основе текущих сумм относительно новой общей суммы
      const normalized = schedule.map((payment) => {
        const price = payment.price || 0;
        const percentage =
          totalAmount > 0
            ? Math.round((price / totalAmount) * 100 * 100) / 100
            : 0;
        return {
          ...payment,
          percentage,
          price: Math.round((totalAmount * percentage) / 100),
        };
      });

      // Проверяем итоговую сумму после нормализации и корректируем последний элемент при необходимости
      const normalizedTotal = normalized.reduce(
        (sum, payment) => sum + (payment.price || 0),
        0,
      );
      const adjustment = totalAmount - normalizedTotal;

      if (Math.abs(adjustment) > 0 && normalized.length > 0) {
        const lastIndex = normalized.length - 1;
        normalized[lastIndex] = {
          ...normalized[lastIndex],
          price: normalized[lastIndex].price + adjustment,
          percentage:
            totalAmount > 0
              ? Math.round(
                  ((normalized[lastIndex].price + adjustment) / totalAmount) *
                    100 *
                    100,
                ) / 100
              : 0,
        };
      }

      return normalized;
    }

    // Если суммы совпадают, просто пересчитываем проценты для точности
    const normalized = schedule.map((payment) => {
      const price = payment.price || 0;
      const percentage =
        totalAmount > 0
          ? Math.round((price / totalAmount) * 100 * 100) / 100
          : 0;
      return {
        ...payment,
        percentage,
        price: Math.round((totalAmount * percentage) / 100),
      };
    });

    // Проверяем итоговую сумму и корректируем последний элемент при необходимости
    const normalizedTotal = normalized.reduce(
      (sum, payment) => sum + (payment.price || 0),
      0,
    );
    const adjustment = totalAmount - normalizedTotal;

    if (Math.abs(adjustment) > 0 && normalized.length > 0) {
      const lastIndex = normalized.length - 1;
      normalized[lastIndex] = {
        ...normalized[lastIndex],
        price: normalized[lastIndex].price + adjustment,
        percentage:
          totalAmount > 0
            ? Math.round(
                ((normalized[lastIndex].price + adjustment) / totalAmount) *
                  100 *
                  100,
              ) / 100
            : 0,
      };
    }

    return normalized;
  };

  // Загрузка данных коммерческого предложения и компаний
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        console.log("projectId не передан:", projectId);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log("Загружаем данные для проекта:", projectId);

        const [
          commercialData,
          ourCompanies,
          counterpartiesList,
          projectDataResponse,
          approvals,
          teisData,
        ] = await Promise.all([
          getCommercialProposal(projectId),
          getAllCounterparties({ counterparty_side: "our_company" }),
          getAllCounterparties({ counterparty_side: "counterparty" }),
          getProjectById(projectId),
          getCommercialProposalApprovals(projectId).catch(() => []),
          getCpTeis(projectId).catch(() => null),
        ]);

        console.log("Полученные данные:", commercialData);
        console.log("Контрагенты (наши компании):", ourCompanies);
        console.log("Контрагенты (получатели):", counterpartiesList);
        console.log("Данные проекта:", projectDataResponse);
        console.log("Согласования:", approvals);
        console.log("Данные ТЭП:", teisData);
        console.log(
          "smeta_documentation_percent:",
          commercialData?.smeta_documentation_percent,
        );
        console.log(
          "project_documentation блоки:",
          commercialData?.project_documentation,
        );
        // Логируем данные паркинга для отладки
        if (commercialData?.project_documentation) {
          const parkingBlock = commercialData.project_documentation.find(
            (block) => block.block?.name?.toLowerCase().includes("паркинг"),
          );
          if (parkingBlock) {
            console.log("Данные паркинга из API:", {
              name: parkingBlock.block?.name,
              floor_count: parkingBlock.floor_count,
              total_area: parkingBlock.total_area,
              coefficient: parkingBlock.coefficient,
            });
          }
        }

        // Нормализуем график платежей на основе итоговой суммы (без НДС)
        const totalAmount =
          commercialData?.total_without_nds ?? commercialData?.total ?? 0;
        if (
          commercialData?.cp_payment_schedule &&
          commercialData.cp_payment_schedule.length > 0 &&
          totalAmount > 0
        ) {
          const normalizedSchedule = normalizePaymentSchedule(
            commercialData.cp_payment_schedule,
            totalAmount,
          );

          // Проверяем, нужно ли обновить график платежей
          const currentTotal = commercialData.cp_payment_schedule.reduce(
            (sum, payment) => sum + (payment.price || 0),
            0,
          );

          if (Math.abs(currentTotal - totalAmount) > 1) {
            // Обновляем график платежей нормализованными данными
            commercialData.cp_payment_schedule = normalizedSchedule;
            console.log(
              "График платежей нормализован на основе итоговой суммы:",
              totalAmount,
            );
          }
        }

        setCommercialData(enrichCommercialDataWithTeis(commercialData, teisData));
        setDesignersCounterparties(ourCompanies || []);
        setRecipientsCounterparties(counterpartiesList || []);
        setProjectData(projectDataResponse);

        // Генпроектировщик: из КП (counterparty_id / counterparty) или первый из наших компаний
        const counterpartyFromOffer = commercialData?.counterparty;
        const counterpartyIdFromOffer = commercialData?.counterparty_id;
        if (ourCompanies?.length > 0) {
          let designer = null;
          if (counterpartyIdFromOffer != null) {
            designer = ourCompanies.find(
              (c) => normalizeId(c.id) === normalizeId(counterpartyIdFromOffer),
            );
          } else if (counterpartyFromOffer) {
            // Ищем по полям из нового формата API
            designer = ourCompanies.find(
              (c) =>
                (counterpartyFromOffer.short_company_name &&
                  c.short_company_name ===
                    counterpartyFromOffer.short_company_name) ||
                (counterpartyFromOffer.full_company_name &&
                  c.full_company_name ===
                    counterpartyFromOffer.full_company_name) ||
                (counterpartyFromOffer.full_name &&
                  c.full_name === counterpartyFromOffer.full_name),
            );
          }
          const designerToSelect = designer || ourCompanies[0];
          setSelectedDesigner({
            value: normalizeId(designerToSelect.id),
            label: getCounterpartyLabel(designerToSelect),
          });
        }

        // Получатель: из КП (receiver_id / receiver) или первый из контрагентов
        const receiverFromOffer = commercialData?.receiver;
        const receiverIdFromOffer = commercialData?.receiver_id;
        if (counterpartiesList?.length > 0) {
          let recipient = null;
          if (receiverIdFromOffer != null) {
            recipient = counterpartiesList.find(
              (c) => normalizeId(c.id) === normalizeId(receiverIdFromOffer),
            );
          } else if (receiverFromOffer) {
            // Ищем по полям из нового формата API (full_name, full_company_name, short_company_name)
            const receiverName =
              receiverFromOffer.short_company_name ||
              receiverFromOffer.full_company_name ||
              receiverFromOffer.full_name ||
              receiverFromOffer.name; // fallback для старого формата
            if (receiverName) {
              recipient = counterpartiesList.find(
                (c) =>
                  c.short_company_name === receiverName ||
                  c.full_company_name === receiverName ||
                  c.full_name === receiverName,
              );
            }
          }
          const recipientToSelect = recipient || counterpartiesList[0];
          setSelectedRecipient({
            value: normalizeId(recipientToSelect.id),
            label: getCounterpartyLabel(recipientToSelect),
          });
        }

        // Редактируемые блоки доп. информации и подписи
        const cd = commercialData;
        if (cd?.design_sections_title != null)
          setDesignSectionsTitle(cd.design_sections_title);
        if (cd?.design_sections_items?.length)
          setDesignSectionsItems(cd.design_sections_items);
        if (cd?.excluded_costs_title != null)
          setExcludedCostsTitle(cd.excluded_costs_title);
        if (cd?.excluded_costs_items?.length)
          setExcludedCostsItems(cd.excluded_costs_items);
        if (cd?.deadlines_text != null) setDeadlinesText(cd.deadlines_text);
        // performer_name теперь приходит напрямую из API
        if (cd?.performer_name != null && cd.performer_name !== "")
          setPerformerName(cd.performer_name);
        if (cd?.signature_date != null) setSignatureDate(cd.signature_date);
        // Ставка НДС из данных КП
        if (cd?.nds_percent != null) setNdsRate(cd.nds_percent);

        setApprovalStatuses(approvals || []);

        // Устанавливаем статус согласования КП из ТЭП или из данных коммерческого предложения
        if (teisData && teisData.cp_approval_status) {
          setCpApprovalStatus(teisData.cp_approval_status);
        } else if (commercialData && commercialData.cp_approval_status) {
          setCpApprovalStatus(commercialData.cp_approval_status);
        } else {
          setCpApprovalStatus(null);
        }

        // Определяем редактируемость на основе версий и статусов
        setIsEditable(
          determineEditable(commercialData, approvals || [], teisData),
        );
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  // Выбранный контрагент — генпроектировщик (наша компания)
  const selectedDesignerCounterparty = selectedDesigner
    ? designersCounterparties.find(
        (c) => normalizeId(c.id) === selectedDesigner.value,
      )
    : designersCounterparties[0];
  const selectedDesignerDirectorName = getDirectorNameFromCounterparty(
    selectedDesignerCounterparty,
  );
  const showDirectorTitle = selectedDesignerCounterparty?.type !== "physical_entity";

  // Выбранный контрагент — получатель
  const selectedRecipientCounterparty = selectedRecipient
    ? recipientsCounterparties.find(
        (c) => normalizeId(c.id) === selectedRecipient.value,
      )
    : recipientsCounterparties[0];

  const formatCounterpartyAddress = (c) => {
    if (!c) return "";
    const parts = [
      c.postal_code,
      c.country,
      c.city,
      c.street,
      c.building,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Функция для формирования URL логотипа компании
  const getCompanyLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;

    // Проверяем, является ли URL полным или относительным
    let imageUrl = logoUrl;

    if (!imageUrl.startsWith("http")) {
      // Если это не полный URL, формируем правильный путь для изображения
      if (imageUrl.startsWith("/")) {
        // Если путь начинается с /, добавляем базовый URL без дополнительного /
        imageUrl = `${getAPIURL().replace(/\/$/, "")}${imageUrl}`;
      } else {
        // Если это только имя файла, формируем полный путь к изображению компании
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (isLocalhost) {
          // На локальной машине используем старый путь
          imageUrl = `${getAPIURL()}images/company_logos/${imageUrl}`;
        } else {
          // На серверах используем новый путь
          imageUrl = `${getAPIURL()}images/company_logos/${imageUrl}`;
        }
      }
    }

    // Декодируем URL для правильной обработки пробелов и специальных символов
    const finalUrl = decodeURIComponent(imageUrl);

    // Отладочная информация
    console.log("=== ОТЛАДКА ЛОГОТИПА ===");
    console.log("Исходный logoUrl:", logoUrl);
    console.log("Сформированный imageUrl:", imageUrl);
    console.log("Финальный URL после декодирования:", finalUrl);

    return finalUrl;
  };

  // Обработчик ошибки загрузки логотипа
  const handleLogoError = (companyId) => {
    setLogoErrors((prev) => new Set([...prev, companyId]));
  };

  // Функции для редактирования полей
  const handleStartEdit = () => {
    if (!effectiveIsEditable) return; // Не редактируем если редактирование заблокировано
    setTempProjectName(commercialData?.project_name || "");
    setTempTitle(commercialData?.title || "");
    setIsEditingProjectName(true);
    setIsEditingTitle(true);
  };

  const handleStartEditPaymentSchedule = () => {
    if (!effectiveIsEditable) return; // Не редактируем если редактирование заблокировано
    const totalAmount = getTotalAmount();
    const existingSchedule = commercialData?.cp_payment_schedule || [];

    // Нормализуем график платежей на основе текущей итоговой суммы
    const normalizedSchedule = normalizePaymentSchedule(
      existingSchedule,
      totalAmount,
    );

    setTempPaymentSchedule(normalizedSchedule);
    setIsEditingPaymentSchedule(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProjectName(false);
    setIsEditingTitle(false);
    setTempProjectName("");
    setTempTitle("");
  };

  const handleCancelEditPaymentSchedule = () => {
    setIsEditingPaymentSchedule(false);
    setTempPaymentSchedule([]);
  };

  const handleSaveTitle = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано
    if (!tempProjectName.trim() || !tempTitle.trim()) {
      alert("Пожалуйста, заполните все поля");
      return;
    }

    setIsSavingTitle(true);
    try {
      const dataToSave = {
        project_name: tempProjectName.trim(),
        title: tempTitle.trim(),
        performer_name: performerName.trim(),
        nds_percent: ndsRate,
      };
      if (selectedDesigner?.value)
        dataToSave.counterparty_id = Number(selectedDesigner.value);
      if (selectedRecipient?.value)
        dataToSave.receiver_id = Number(selectedRecipient.value);
      console.log("Отправка данных для сохранения CP title:", {
        projectId,
        data: dataToSave,
      });

      const result = await saveCpTitle(projectId, dataToSave);

      // Обновляем локальные данные, включая версию из ответа
      setCommercialData((prev) => ({
        ...prev,
        project_name: tempProjectName.trim(),
        title: tempTitle.trim(),
        performer_name: performerName.trim(),
        ...(selectedDesigner?.value && {
          counterparty_id: Number(selectedDesigner.value),
        }),
        ...(selectedRecipient?.value && {
          receiver_id: Number(selectedRecipient.value),
        }),
        ...(result?.version !== undefined && { version: result.version }),
      }));

      setIsEditingProjectName(false);
      setIsEditingTitle(false);
      setTempProjectName("");
      setTempTitle("");
    } catch (error) {
      console.error("Ошибка при сохранении:", error);
      const errorMessage =
        error.message || "Неизвестная ошибка при сохранении данных";
      alert(`Ошибка при сохранении данных: ${errorMessage}`);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleSavePaymentSchedule = async () => {
    if (!effectiveIsEditable) return; // Не сохраняем если редактирование заблокировано
    if (tempPaymentSchedule.length === 0) {
      alert("Пожалуйста, добавьте хотя бы один элемент в график платежей");
      return;
    }

    // Проверяем, что все поля заполнены
    const hasEmptyFields = tempPaymentSchedule.some(
      (payment) =>
        !payment.work_name?.trim() ||
        payment.percentage === undefined ||
        payment.percentage === null ||
        payment.price === undefined ||
        payment.price === null ||
        !payment.deadline_description?.trim(),
    );

    if (hasEmptyFields) {
      alert("Пожалуйста, заполните все поля в графике платежей");
      return;
    }

    // Получаем итоговую сумму из верхней таблицы
    const totalAmount = getTotalAmount();

    // Вычисляем текущую сумму всех платежей до нормализации
    const currentTotal = tempPaymentSchedule.reduce(
      (sum, payment) => sum + (payment.price || 0),
      0,
    );

    // Нормализуем график платежей, чтобы сумма совпадала с итоговой
    const normalizedSchedule = normalizePaymentSchedule(
      tempPaymentSchedule,
      totalAmount,
    );

    setIsSavingPaymentSchedule(true);
    try {
      await saveCpPaymentSchedule(projectId, normalizedSchedule);

      // Обновляем локальные данные
      setCommercialData((prev) => ({
        ...prev,
        cp_payment_schedule: normalizedSchedule,
      }));

      setIsEditingPaymentSchedule(false);
      setTempPaymentSchedule([]);

      // Показываем сообщение, если данные были нормализованы
      const normalizedTotal = normalizedSchedule.reduce(
        (sum, payment) => sum + (payment.price || 0),
        0,
      );
      if (Math.abs(currentTotal - totalAmount) > 1) {
        alert(
          `График платежей был нормализован на основе итоговой суммы ${totalAmount.toLocaleString(
            "ru-RU",
          )} тенге. Сумма всех платежей теперь составляет ${normalizedTotal.toLocaleString(
            "ru-RU",
          )} тенге.`,
        );
      }
    } catch (error) {
      console.error("Ошибка при сохранении графика платежей:", error);
      alert("Ошибка при сохранении графика платежей");
    } finally {
      setIsSavingPaymentSchedule(false);
    }
  };

  // Функции для управления элементами графика платежей
  const addPaymentItem = () => {
    if (!effectiveIsEditable) return; // Не добавляем если редактирование заблокировано
    setTempPaymentSchedule([
      ...tempPaymentSchedule,
      {
        work_name: "",
        percentage: 0,
        price: 0,
        deadline_description: "",
      },
    ]);
  };

  const removePaymentItem = (index) => {
    if (!effectiveIsEditable) return; // Не удаляем если редактирование заблокировано
    setTempPaymentSchedule(tempPaymentSchedule.filter((_, i) => i !== index));
  };

  // Получаем общую сумму для расчета процентов
  const getTotalAmount = () => {
    return commercialData?.total_without_nds ?? commercialData?.total ?? 0;
  };

  // Вычисляем площади для сметной документации с округлением
  const calculateSmetaAreas = () => {
    if (!commercialData) return { residentialArea: 0, parkingArea: 0 };

    // Получаем суммы сметной документации
    const residentialSum = commercialData.smeta_documentation?.residential || 0;
    const parkingSum = commercialData.smeta_documentation?.parking || 0;
    const smetaPercent = commercialData.smeta_documentation_percent || 0;

    // Используем процент с бэка как цену за единицу для расчета площадей
    // Суммы приходят округленные с бэка, вычисляем площади обратно
    let residentialArea = 0;
    let parkingArea = 0;

    if (smetaPercent > 0) {
      if (residentialSum > 0) {
        residentialArea = Math.round(residentialSum / smetaPercent);
      }

      if (parkingSum > 0) {
        parkingArea = Math.round(parkingSum / smetaPercent);
      }
    }

    return { residentialArea, parkingArea };
  };

  // Обработчик фокуса для числовых полей
  const handleNumberFieldFocus = (e) => {
    if (e.target.value === "0") {
      const input = e.target;
      requestAnimationFrame(() => input.select());
    }
  };

  // Обработчик ввода для числовых полей
  const handleNumberFieldInput = (index, field, e) => {
    let value = e.target.value;

    // Убираем ведущие нули
    if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
      value = value.replace(/^0+/, "");
      if (value === "") value = "0";
    }

    updatePaymentItem(index, field, parseFloat(value) || 0);
  };

  // Обновляем элемент с автоматическим пересчетом
  const updatePaymentItem = (index, field, value) => {
    if (!effectiveIsEditable) return; // Не обновляем если редактирование заблокировано
    const totalAmount = getTotalAmount();

    // Добавляем анимацию для пересчитываемого поля
    const calculatedField = field === "percentage" ? "price" : "percentage";
    const fieldKey = `${index}-${calculatedField}`;

    setCalculatingFields((prev) => new Set([...prev, fieldKey]));

    // Убираем анимацию через 300ms
    setTimeout(() => {
      setCalculatingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }, 300);

    setTempPaymentSchedule(
      tempPaymentSchedule.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };

          // Если изменился процент - пересчитываем сумму
          if (field === "percentage") {
            const percentage = parseFloat(value) || 0;
            updatedItem.price = Math.round((totalAmount * percentage) / 100);
          }

          // Если изменилась сумма - пересчитываем процент
          if (field === "price") {
            const price = parseFloat(value) || 0;
            updatedItem.percentage =
              totalAmount > 0
                ? Math.round((price / totalAmount) * 100 * 100) / 100
                : 0;
          }

          return updatedItem;
        }
        return item;
      }),
    );
  };

  // Данные для селектов: генпроектировщики и получатели из контрагентов
  const designerOptions = [
    CREATE_DESIGNER_OPTION,
    ...designersCounterparties.map((c) => ({
      value: normalizeId(c.id),
      label: getCounterpartyLabel(c),
    })),
  ];

  const recipientOptions = [
    CREATE_RECIPIENT_OPTION,
    ...recipientsCounterparties.map((c) => ({
      value: normalizeId(c.id),
      label: getCounterpartyLabel(c),
    })),
  ];

  const [ndsRate, setNdsRate] = useState(12);
  const [isSavingNdsRate, setIsSavingNdsRate] = useState(false);

  // Сохранение выбора генпроектировщика и получателя на бэкенд
  const saveDesignerAndRecipient = async () => {
    if (
      !projectId ||
      !selectedDesigner?.value ||
      !selectedRecipient?.value ||
      !effectiveIsEditable
    )
      return;
    try {
      const dataToSave = {
        project_name: commercialData?.project_name ?? "",
        title: commercialData?.title ?? "",
        counterparty_id: Number(selectedDesigner.value),
        receiver_id: Number(selectedRecipient.value),
        performer_name: performerName.trim(),
        nds_percent: ndsRate,
      };
      const result = await saveCpTitle(projectId, dataToSave);
      setCommercialData((prev) => ({
        ...prev,
        counterparty_id: Number(selectedDesigner.value),
        receiver_id: Number(selectedRecipient.value),
        ...(result?.version !== undefined && { version: result.version }),
      }));
    } catch (err) {
      console.error("Ошибка сохранения генпроектировщика/получателя:", err);
      alert(err?.message || "Ошибка сохранения");
    }
  };

  const handleSaveAdditionalInfo = async () => {
    if (!projectId || !effectiveIsEditable) return;
    setIsSavingAdditionalInfo(true);
    try {
      const dataToSave = {
        project_name: commercialData?.project_name ?? "",
        title: commercialData?.title ?? "",
        performer_name: performerName.trim(),
        nds_percent: ndsRate,
      };
      if (selectedDesigner?.value)
        dataToSave.counterparty_id = Number(selectedDesigner.value);
      if (selectedRecipient?.value)
        dataToSave.receiver_id = Number(selectedRecipient.value);
      const result = await saveCpTitle(projectId, dataToSave);
      setCommercialData((prev) => ({
        ...prev,
        project_name: dataToSave.project_name,
        title: dataToSave.title,
        performer_name: dataToSave.performer_name,
        design_sections_title: designSectionsTitle.trim(),
        design_sections_items: designSectionsItems
          .map((s) => s.trim())
          .filter(Boolean),
        excluded_costs_title: excludedCostsTitle.trim(),
        excluded_costs_items: excludedCostsItems
          .map((s) => s.trim())
          .filter(Boolean),
        deadlines_text: deadlinesText.trim(),
        signature_date: signatureDate.trim(),
        ...(result?.version !== undefined && { version: result.version }),
      }));
      setIsEditingAdditionalInfo(false);
      alert("Дополнительная информация сохранена.");
    } catch (err) {
      console.error("Ошибка сохранения доп. информации:", err);
      alert(err?.message || "Ошибка сохранения");
    } finally {
      setIsSavingAdditionalInfo(false);
    }
  };

  const handleCancelEditAdditionalInfo = () => {
    setIsEditingAdditionalInfo(false);
  };

  const handleApprovalSubmit = async (selectedApprovers) => {
    if (!projectId || !commercialData) {
      alert("Ошибка: отсутствуют данные проекта или коммерческого предложения");
      return;
    }

    setIsSubmittingApproval(true);
    try {
      const totalPrice =
        commercialData?.total_without_nds ?? commercialData?.total ?? 0;

      console.log("Отправка на согласование:", {
        projectId,
        approverIds: selectedApprovers,
        totalPrice,
      });

      const result = await requestCommercialProposalApproval(
        projectId,
        selectedApprovers,
        totalPrice,
      );

      console.log("Результат отправки на согласование:", result);
      console.log("Версия из ответа API:", result?.version);

      // Обновляем статусы согласования
      const statuses = await getCommercialProposalApprovals(projectId);
      console.log("Полученные статусы согласований:", statuses);
      console.log(
        "Версии в статусах:",
        statuses?.map((s) => ({ id: s.id, version: s.version })),
      );
      setApprovalStatuses(statuses || []);

      // Перезагружаем данные коммерческого предложения, чтобы обновился флаг editable
      const updatedData = await getCommercialProposal(projectId);
      const updatedTeisData = await getCpTeis(projectId).catch(() => null);

      setCommercialData(enrichCommercialDataWithTeis(updatedData, updatedTeisData));
      // Устанавливаем статус согласования КП из ТЭП или из данных коммерческого предложения
      if (updatedTeisData && updatedTeisData.cp_approval_status) {
        setCpApprovalStatus(updatedTeisData.cp_approval_status);
      } else if (updatedData && updatedData.cp_approval_status) {
        setCpApprovalStatus(updatedData.cp_approval_status);
      } else {
        setCpApprovalStatus(null);
      }
      setIsEditable(
        determineEditable(updatedData, statuses || [], updatedTeisData),
      );

      // Показываем успешное сообщение
      alert("Коммерческое предложение успешно отправлено на согласование!");

      // Закрываем модальное окно
      setShowApprovalModal(false);
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);
      alert(`Ошибка при отправке на согласование: ${error.message}`);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const getStatusIcon = (status) => {
    if (!status)
      return (
        <span className="commercial-offer-approval-status-icon pending">
          ⏳
        </span>
      );
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return (
        <span className="commercial-offer-approval-status-icon approved">
          ✓
        </span>
      );
    }
    if (statusLower === "на рассмотрении" || statusLower === "pending") {
      return (
        <span className="commercial-offer-approval-status-icon pending">
          ⏳
        </span>
      );
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return (
        <span className="commercial-offer-approval-status-icon rejected">
          ✕
        </span>
      );
    }
    return (
      <span className="commercial-offer-approval-status-icon pending">⏳</span>
    );
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return "Согласован";
    }
    if (
      statusLower === "на рассмотрении" ||
      statusLower === "pending" ||
      statusLower === "на рассмотрении"
    ) {
      return "На рассмотрении";
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return "Отклонен";
    }
    return "На рассмотрении";
  };

  // Обработчик отмены согласования
  const handleCancelApproval = async () => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите отозвать отправку на согласование?",
      )
    ) {
      return;
    }

    try {
      setIsCancellingApproval(true);
      await cancelCommercialProposalApproval(parseInt(projectId));

      // Обновляем статусы согласования с кэш-бастингом
      const statuses = await getCommercialProposalApprovals(projectId);
      setApprovalStatuses(statuses || []);

      // Перезагружаем данные коммерческого предложения, чтобы обновился флаг editable
      const updatedData = await getCommercialProposal(projectId);
      const updatedTeisData = await getCpTeis(projectId).catch(() => null);

      setCommercialData(enrichCommercialDataWithTeis(updatedData, updatedTeisData));
      // Устанавливаем статус согласования КП из ТЭП или из данных коммерческого предложения
      if (updatedTeisData && updatedTeisData.cp_approval_status) {
        setCpApprovalStatus(updatedTeisData.cp_approval_status);
      } else if (updatedData && updatedData.cp_approval_status) {
        setCpApprovalStatus(updatedData.cp_approval_status);
      } else {
        setCpApprovalStatus(null);
      }
      setIsEditable(
        determineEditable(updatedData, statuses || [], updatedTeisData),
      );

      // Показываем успешное сообщение
      alert("Отправка на согласование успешно отозвана!");

      // Перезагружаем страницу через небольшую задержку, чтобы пользователь увидел сообщение
      // Используем жесткую перезагрузку с кэш-бастингом для гарантии свежих данных
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
    } catch (error) {
      console.error("Ошибка при отмене согласования:", error);
      alert(`Ошибка при отмене согласования: ${error.message}`);
    } finally {
      setIsCancellingApproval(false);
    }
  };

  // Проверяем, отменено ли согласование инициатором по статусу из данных КП
  const isCancelledByInitiator =
    cpApprovalStatus &&
    (cpApprovalStatus.toLowerCase() === "отменен инициатором" ||
      cpApprovalStatus.toLowerCase() === "cancelled_by_initiator");

  // Находим активное согласование (только то, которое еще не завершено)
  // Если согласование отменено инициатором по статусу из КП, не ищем активное согласование
  const activeApproval = isCancelledByInitiator
    ? null
    : approvalStatuses.find((approval) => {
        const status = approval.status?.toLowerCase() || "";
        if (
          status === "согласован" ||
          status === "отклонен" ||
          status === "отменен инициатором" ||
          status === "не согласован"
        ) {
          return false; // Согласование уже завершено или отменено
        }

        // Если статус "на рассмотрении" - это активное согласование
        if (status === "на рассмотрении") {
          return true;
        }

        // Проверяем шаги согласования
        if (approval.steps && approval.steps.length > 0) {
          return approval.steps.some((step) => {
            const stepStatus = step.status?.toLowerCase() || "";
            return (
              stepStatus === "на рассмотрении" ||
              stepStatus === "" ||
              step.status === null ||
              step.status === undefined
            );
          });
        }

        // Если шагов нет, но статус не завершен, считаем активным
        return !approval.status || status === "";
      });

  // Проверяем, является ли текущий пользователь инициатором согласования
  const isInitiator =
    activeApproval && userData && activeApproval.initiator_id === userData.id;

  // Показываем кнопку отмены только если:
  // 1. Есть активное согласование
  // 2. Пользователь является инициатором
  // 3. Статус согласования не "отменен инициатором"
  const showCancelButton =
    activeApproval !== undefined &&
    activeApproval !== null &&
    isInitiator &&
    !isCancelledByInitiator;

  // Проверяем, передан ли projectId
  if (!projectId) {
    return (
      <div className="commercial-offer-container">
        <div className="error">Ошибка: ID проекта не передан</div>
      </div>
    );
  }

  return (
    <div className="commercial-offer-container">
      {/* Параметры коммерческого предложения */}
      <div className="commercial-offer-params">
        <div className="commercial-offer-top-bar">
          <h2 className="commercial-offer-title">
            Параметры коммерческого предложения
          </h2>
          <div className="commercial-offer-top-bar-right">
            {isCancelledByInitiator && (
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginRight: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "20px" }}>ℹ️</span>
                <span style={{ color: "#856404", fontSize: "14px" }}>
                  Согласование коммерческого предложения отменено инициатором
                </span>
              </div>
            )}
            {showCancelButton && (
              <>
                {activeApproval.steps && activeApproval.steps.length > 0 && (
                  <div className="commercial-offer-approval-status-block">
                    {activeApproval.steps.map((step, index) => (
                      <div
                        key={step.id || index}
                        className="commercial-offer-approval-step"
                      >
                        <span className="commercial-offer-approver-name">
                          {step.approver_name || "Не указан"}:
                        </span>
                        {getStatusIcon(step.status)}
                        <span className="commercial-offer-step-status-text">
                          {getStatusText(step.status)}
                        </span>
                        {step.comment && (
                          <span className="commercial-offer-step-comment">
                            ({step.comment})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <button
                  className="commercial-offer-cancel-approval-btn"
                  onClick={handleCancelApproval}
                  disabled={isCancellingApproval}
                >
                  {isCancellingApproval ? "Отзыв..." : "Отозвать отправку"}
                </button>
              </>
            )}
            <div className="commercial-offer-version-badge-container">
              {commercialData?.version !== undefined &&
                commercialData?.version !== null && (
                  <span className="commercial-offer-version-badge">
                    Версия {commercialData.version}
                  </span>
                )}
              <button
                className="commercial-offer-history-btn"
                onClick={() => setIsVersionHistoryOpen(true)}
              >
                <span className="commercial-offer-history-icon">⟳</span> История
                версий
              </button>
            </div>
          </div>
        </div>
        {/* Предупреждение о блокировке редактирования */}
        {!effectiveIsEditable && (
          <div
            style={{
              background: "#fff8f1",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              <span style={{ fontSize: "24px" }}>⚠️</span>
              <span style={{ color: "#b26a00", fontSize: "14px" }}>
                Коммерческое предложение находится на этапе согласования,
                поэтому редактирование заблокировано
              </span>
            </div>
            {cpApprovalStatus === "approved" && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Вы уверены, что хотите создать копию? Будет создана новая версия ТЭП, Сметы и КП без согласований.",
                    )
                  ) {
                    return;
                  }

                  try {
                    setIsCreatingCopy(true);
                    await restoreVersion(projectId, null);
                    // Перезагружаем страницу после создания копии
                    setTimeout(() => {
                      window.location.reload();
                    }, 500);
                  } catch (error) {
                    console.error("Ошибка при создании копии:", error);
                    alert(`Ошибка при создании копии: ${error.message}`);
                  } finally {
                    setIsCreatingCopy(false);
                  }
                }}
                disabled={isCreatingCopy}
                style={{
                  padding: "8px 16px",
                  background: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: isCreatingCopy ? "not-allowed" : "pointer",
                  opacity: isCreatingCopy ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {isCreatingCopy ? "Создание копии..." : "Сделать копию"}
              </button>
            )}
          </div>
        )}

        <div className="params-fields">
          <div className="param-field">
            <label>Выберите генпроектировщика*</label>
            <Select
              styles={selectStyles}
              options={designerOptions}
              value={selectedDesigner}
              onChange={(val) => {
                if (val?.value === CREATE_DESIGNER_OPTION.value) {
                  setShowAddDesignerModal(true);
                  return;
                }
                setSelectedDesigner(val);
                if (val?.value && selectedRecipient?.value) {
                  saveDesignerAndRecipient();
                }
              }}
              isDisabled={!effectiveIsEditable}
              placeholder="Выберите проектировщика..."
              formatOptionLabel={(option) =>
                option.value === CREATE_DESIGNER_OPTION.value ? (
                  <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )
              }
            />
          </div>

          <div className="param-field">
            <label>Выберите компанию-получателя*</label>
            <Select
              styles={selectStyles}
              options={recipientOptions}
              value={selectedRecipient}
              onChange={(val) => {
                if (val?.value === CREATE_RECIPIENT_OPTION.value) {
                  setShowAddRecipientModal(true);
                  return;
                }
                setSelectedRecipient(val);
                if (val?.value && selectedDesigner?.value) {
                  saveDesignerAndRecipient();
                }
              }}
              isDisabled={!effectiveIsEditable}
              placeholder="Выберите получателя..."
              formatOptionLabel={(option) =>
                option.value === CREATE_RECIPIENT_OPTION.value ? (
                  <span style={{ color: "#2a5e38", fontWeight: 500 }}>
                    {option.label}
                  </span>
                ) : (
                  option.label
                )
              }
            />
          </div>
        </div>

        <div className="params-fields-second-row">
          <div className="param-field">
            <label>Ставка НДС (%)</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="number"
                value={ndsRate}
                onChange={(e) => setNdsRate(parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="1"
                disabled={!effectiveIsEditable || isSavingNdsRate}
                style={{
                  width: "100px",
                  padding: "8px 12px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <button
                onClick={async () => {
                  if (!projectId || !effectiveIsEditable) return;
                  setIsSavingNdsRate(true);
                  try {
                    const dataToSave = {
                      project_name: commercialData?.project_name ?? "",
                      title: commercialData?.title ?? "",
                      nds_percent: ndsRate,
                      performer_name: performerName.trim(),
                    };
                    if (selectedDesigner?.value)
                      dataToSave.counterparty_id = Number(
                        selectedDesigner.value,
                      );
                    if (selectedRecipient?.value)
                      dataToSave.receiver_id = Number(selectedRecipient.value);
                    await saveCpTitle(projectId, dataToSave);

                    const updatedData = await getCommercialProposal(projectId);
                    const updatedTeisData = await getCpTeis(projectId).catch(
                      () => null,
                    );
                    setCommercialData(
                      enrichCommercialDataWithTeis(updatedData, updatedTeisData),
                    );
                    alert("Ставка НДС сохранена");
                  } catch (error) {
                    console.error("Ошибка при сохранении ставки НДС:", error);
                    alert(`Ошибка: ${error.message}`);
                  } finally {
                    setIsSavingNdsRate(false);
                  }
                }}
                disabled={!effectiveIsEditable || isSavingNdsRate}
                style={{
                  padding: "8px 16px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor:
                    !effectiveIsEditable || isSavingNdsRate
                      ? "not-allowed"
                      : "pointer",
                  opacity: !effectiveIsEditable || isSavingNdsRate ? 0.6 : 1,
                }}
              >
                {isSavingNdsRate ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </div>

        <div className="commercial-offer-links">
          <h3 className="offer-title">Коммерческое предложение</h3>
          {/* <div className="mode-toggle">
            <div className="mode-buttons">
              <button
                className={`mode-btn ${!isEditMode ? "active" : ""}`}
                onClick={() => setIsEditMode(false)}
                title="Просмотр"
                disabled={!effectiveIsEditable}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 2C4.5 2 1.5 4.5 1.5 8C1.5 11.5 4.5 14 8 14C11.5 14 14.5 11.5 14.5 8C14.5 4.5 11.5 2 8 2ZM8 12C5.5 12 3.5 10 3.5 8C3.5 6 5.5 4 8 4C10.5 4 12.5 6 12.5 8C12.5 10 10.5 12 8 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 6C7.5 6 7 6.5 7 7C7 7.5 7.5 8 8 8C8.5 8 9 7.5 9 7C9 6.5 8.5 6 8 6Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={`mode-btn ${isEditMode ? "active" : ""}`}
                onClick={() => setIsEditMode(true)}
                title="Редактирование"
                disabled={!effectiveIsEditable}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 2L14 4L5 13H2V10L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div> */}
        </div>
      </div>

      {/* Документ коммерческого предложения */}
      <div className="commercial-offer-document">
        <div className="document-header">
          <div className="sender-info-latin">
            <div className="company-name">
              {getCounterpartyLabel(selectedDesignerCounterparty)}
            </div>
            {selectedDesignerCounterparty?.organization_type?.kz_name && (
              <div className="company-type">
                {selectedDesignerCounterparty.organization_type.kz_name}
              </div>
            )}
            <div className="company-address">
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
            </div>
            <div className="company-details">
              <div>БИН/ИИН: {selectedDesignerCounterparty?.bin_or_inn}</div>
              <div>тел: {selectedDesignerCounterparty?.phone}</div>
              {selectedDesignerCounterparty?.website && (
                <div>{selectedDesignerCounterparty.website}</div>
              )}
              <div>e-mail: {selectedDesignerCounterparty?.email}</div>
            </div>
          </div>

          {selectedDesignerCounterparty?.logo_url &&
            getCompanyLogoUrl(selectedDesignerCounterparty.logo_url) &&
            !logoErrors.has(selectedDesignerCounterparty.id) && (
              <div className="document-logo">
                <div className="logo-container">
                  {loadingLogos.has(selectedDesignerCounterparty.id) && (
                    <div className="logo-loading">Загрузка...</div>
                  )}
                  <img
                    src={getCompanyLogoUrl(
                      selectedDesignerCounterparty.logo_url,
                    )}
                    alt={getCounterpartyLabel(selectedDesignerCounterparty)}
                    onLoadStart={() => {
                      setLoadingLogos(
                        (prev) =>
                          new Set([...prev, selectedDesignerCounterparty.id]),
                      );
                    }}
                    onError={() => {
                      handleLogoError(selectedDesignerCounterparty.id);
                      setLoadingLogos((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedDesignerCounterparty.id);
                        return newSet;
                      });
                    }}
                    onLoad={() => {
                      setLoadingLogos((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedDesignerCounterparty.id);
                        return newSet;
                      });
                      setLogoErrors((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(selectedDesignerCounterparty.id);
                        return newSet;
                      });
                    }}
                  />
                </div>
              </div>
            )}

          <div className="sender-info-cyrillic">
            {selectedDesignerCounterparty?.organization_type?.name && (
              <div className="company-type-ru">
                {selectedDesignerCounterparty.organization_type.name}
              </div>
            )}
            <div className="company-name">
              {getCounterpartyLabel(selectedDesignerCounterparty)}
            </div>
            <div className="company-address-ru">
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
            </div>
            <div className="company-details">
              <div>БИН/ИИН: {selectedDesignerCounterparty?.bin_or_inn}</div>
              <div>тел: {selectedDesignerCounterparty?.phone}</div>
              {selectedDesignerCounterparty?.website && (
                <div>{selectedDesignerCounterparty.website}</div>
              )}
              <div>e-mail: {selectedDesignerCounterparty?.email}</div>
            </div>
          </div>
        </div>

        <div className="document-bottom">
          <div className="outgoing-info">
            <div className="outgoing-number">
              <strong>исх. №</strong> _________________
            </div>
            <div className="outgoing-date">
              <strong>от</strong> "__" ______ 20__ г.
            </div>
          </div>
          <div className="recipient-company">
            <strong>
              {selectedRecipientCounterparty
                ? getCounterpartyLabel(selectedRecipientCounterparty)
                : ""}
            </strong>
          </div>
        </div>

        <div className="document-title">КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ</div>

        <div className="document-content">
          <p className="text-center">
            Настоящим выражаем свое почтение и признательность за возможность
            участвовать в проекте по строительству объекта:
          </p>
          <div className="text-center project-name">
            {isEditingProjectName ? (
              <div className="editable-field">
                <label className="edit-label">Название проекта:</label>
                <input
                  type="text"
                  value={tempProjectName}
                  onChange={(e) => setTempProjectName(e.target.value)}
                  className="edit-input"
                  placeholder="Введите название проекта"
                  disabled={!effectiveIsEditable}
                />
              </div>
            ) : (
              <div className="display-field">
                <span>
                  "
                  {commercialData?.project_name ||
                    "Многоквартирный жилой комплекс со встроенными помещениями и паркингом, расположенный по адресу: г. Астана, Есильский район, улица N"}
                  "
                </span>
                <button
                  className="edit-btn"
                  onClick={handleStartEdit}
                  title="Редактировать проект"
                  disabled={!effectiveIsEditable}
                  style={{
                    cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                    opacity: !effectiveIsEditable ? 0.5 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 2L14 4L5 13H2V10L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="text-center">
            {isEditingTitle ? (
              <div className="editable-field">
                <label className="edit-label">Описание проекта:</label>
                <textarea
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="edit-textarea"
                  placeholder="Введите описание проекта"
                  rows="3"
                  disabled={!effectiveIsEditable}
                />
              </div>
            ) : (
              <div className="display-field">
                <span>
                  {commercialData?.title ||
                    "После мониторинга объекта и общей площади этажа предлагаем разработать рабочий проект в количестве 5 блоков и паркинга"}
                </span>
                <button
                  className="edit-btn"
                  onClick={handleStartEdit}
                  title="Редактировать проект"
                  disabled={!effectiveIsEditable}
                  style={{
                    cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                    opacity: !effectiveIsEditable ? 0.5 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 2L14 4L5 13H2V10L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Кнопки сохранения/отмены */}
          {(isEditingProjectName || isEditingTitle) && (
            <div className="edit-actions">
              <button
                className="save-btn"
                onClick={handleSaveTitle}
                disabled={!effectiveIsEditable || isSavingTitle}
              >
                {isSavingTitle ? (
                  "Сохранение..."
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13 2L6 9L3 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Сохранить
                  </>
                )}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelEdit}
                disabled={!effectiveIsEditable || isSavingTitle}
              >
                Отмена
              </button>
            </div>
          )}
          <div className="document-cost">
            <strong>
              за стоимость{" "}
              {(
                commercialData?.total_with_nds ||
                commercialData?.total_without_nds ||
                commercialData?.total
              )?.toLocaleString("ru-RU") || "0"}{" "}
              тенге
              {commercialData?.nds_percent > 0
                ? ` с учетом НДС (${commercialData.nds_percent}%)`
                : " без НДС"}
            </strong>
          </div>
        </div>

        {/* Таблица работ */}
        <div className="work-table">
          <h3>Наименование работ</h3>
          {loading ? (
            <div className="loading">Загрузка данных...</div>
          ) : error ? (
            <div className="error">Ошибка загрузки: {error}</div>
          ) : commercialData ? (
            <table>
              <thead>
                <tr>
                  <th>№</th>
                  <th>Наименование работ</th>
                  <th>Часть здания</th>
                  <th>Коэффициент привязки</th>
                  <th>Площадь, м2</th>
                  <th>Коэффициент этажности</th>
                  <th>Цена за ед.</th>
                  <th>ИТОГО</th>
                </tr>
              </thead>
              <tbody>
                {/* Проектная документация */}
                <tr className="commercial-section-header">
                  <td>1</td>
                  <td>Разработка проектной документации</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                {commercialData.project_documentation?.map((block, index) => (
                  <tr
                    key={block.block.id}
                    className={index % 2 === 0 ? "table-row" : "table-row-alt"}
                  >
                    <td></td>
                    <td></td>
                    <td>
                      {block.block.name} - {block.floor_count}эт
                    </td>
                    <td>{block.coefficient?.toFixed(2) || "1,00"}</td>
                    <td>{block.total_area?.toLocaleString("ru-RU") || "0"}</td>
                    <td>{(block.attachment_coeff ?? 0).toFixed(2)}</td>
                    <td>{block.price_per_m2?.toLocaleString("ru-RU") || ""}</td>
                    <td>
                      {(block.total_without_nds ?? block.total)?.toLocaleString(
                        "ru-RU",
                      ) || "0"}
                    </td>
                  </tr>
                ))}
                <tr className="section-total">
                  <td colSpan="4">ВСЕГО</td>
                  <td>
                    {commercialData.project_documentation
                      ?.reduce((sum, block) => sum + (block.total_area || 0), 0)
                      .toLocaleString("ru-RU")}
                  </td>
                  <td></td>
                  <td></td>
                  <td>
                    {commercialData.project_documentation
                      ?.reduce(
                        (sum, block) =>
                          sum +
                          ((block.total_without_nds ?? block.total) || 0),
                        0,
                      )
                      .toLocaleString("ru-RU")}
                  </td>
                </tr>
                {/* Сметная документация */}
                <tr className="commercial-section-header">
                  <td>2</td>
                  <td>Разработка сметной документации</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                {(() => {
                  const { residentialArea, parkingArea } =
                    calculateSmetaAreas();
                  return (
                    <>
                      {commercialData.smeta_documentation?.residential &&
                      commercialData.smeta_documentation.residential > 0 ? (
                        <tr className="table-row">
                          <td></td>
                          <td></td>
                          <td>Жилая часть</td>
                          <td></td>
                          <td></td>
                          <td>
                            {residentialArea > 0
                              ? residentialArea.toLocaleString("ru-RU")
                              : ""}
                          </td>
                          <td>
                            {commercialData.smeta_documentation_percent
                              ? `${commercialData.smeta_documentation_percent}`
                              : ""}
                          </td>
                          <td>
                            {commercialData.smeta_documentation.residential?.toLocaleString(
                              "ru-RU",
                            ) || "0"}
                          </td>
                        </tr>
                      ) : null}
                      {commercialData.smeta_documentation?.parking &&
                      commercialData.smeta_documentation.parking > 0 ? (
                        <tr className="table-row-alt">
                          <td></td>
                          <td></td>
                          <td>Паркинг</td>
                          <td></td>
                          <td></td>
                          <td>
                            {parkingArea > 0
                              ? parkingArea.toLocaleString("ru-RU")
                              : ""}
                          </td>
                          <td>
                            {commercialData.smeta_documentation_percent
                              ? `${commercialData.smeta_documentation_percent}`
                              : ""}
                          </td>
                          <td>
                            {commercialData.smeta_documentation.parking?.toLocaleString(
                              "ru-RU",
                            ) || "0"}
                          </td>
                        </tr>
                      ) : null}
                    </>
                  );
                })()}
                <tr className="section-total">
                  <td colSpan="4">ВСЕГО</td>
                  <td>
                    {(() => {
                      const { residentialArea, parkingArea } =
                        calculateSmetaAreas();
                      const totalArea = residentialArea + parkingArea;
                      return totalArea > 0
                        ? totalArea.toLocaleString("ru-RU")
                        : "";
                    })()}
                  </td>
                  <td></td>
                  <td></td>
                  <td>
                    {(
                      (commercialData.smeta_documentation?.residential || 0) +
                      (commercialData.smeta_documentation?.parking || 0)
                    ).toLocaleString("ru-RU")}
                  </td>
                </tr>
                {/* BIM надбавка */}
                {commercialData.bim_surcharge &&
                commercialData.bim_surcharge.price > 0 ? (
                  <tr className="commercial-section-header">
                    <td>3</td>
                    <td>Надбавка за проектирование рабочего проекта по BIM</td>
                    <td>
                      Надбавка за BIM - {commercialData.bim_surcharge.percent}%
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      {commercialData.bim_surcharge.price?.toLocaleString(
                        "ru-RU",
                      ) || "0"}
                    </td>
                  </tr>
                ) : null}
                {/* Надбавка за этажность */}
                {commercialData.floor_surcharge &&
                commercialData.floor_surcharge.price > 0 ? (
                  <tr className="commercial-section-header">
                    <td>4</td>
                    <td>Надбавка за этажность</td>
                    <td>
                      Коэффициент{" "}
                      {(
                        commercialData.floor_surcharge.percent / 100 +
                        1
                      ).toFixed(2)}{" "}
                      к общей площади
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      {commercialData.floor_surcharge.price?.toLocaleString(
                        "ru-RU",
                      ) || "0"}
                    </td>
                  </tr>
                ) : null}
                {/* Надбавка за ускорение */}
                {commercialData.acceleration_surcharge &&
                commercialData.acceleration_surcharge.price > 0 ? (
                  <tr className="commercial-section-header">
                    <td>5</td>
                    <td>Надбавка за ускорение</td>
                    <td>
                      Надбавка за ускорение -{" "}
                      {commercialData.acceleration_surcharge.percent}%
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      {commercialData.acceleration_surcharge.price?.toLocaleString(
                        "ru-RU",
                      ) || "0"}
                    </td>
                  </tr>
                ) : null}
                {/* Общая строка ВСЕГО для всех надбавок */}
                {((commercialData.bim_surcharge &&
                  commercialData.bim_surcharge.price > 0) ||
                  (commercialData.floor_surcharge &&
                    commercialData.floor_surcharge.price > 0) ||
                  (commercialData.acceleration_surcharge &&
                    commercialData.acceleration_surcharge.price > 0)) && (
                  <tr className="section-total">
                    <td colSpan="5">ВСЕГО</td>
                    <td></td>
                    <td></td>
                    <td>
                      {(
                        (commercialData.bim_surcharge?.price || 0) +
                        (commercialData.floor_surcharge?.price || 0) +
                        (commercialData.acceleration_surcharge?.price || 0)
                      ).toLocaleString("ru-RU")}
                    </td>
                  </tr>
                )}
                {/* Итоговая сумма */}
                <tr className="summary-row">
                  <td>6</td>
                  <td colSpan="6">Стоимость проектных работ</td>
                  <td>
                    {(
                      commercialData.total_without_nds ?? commercialData.total
                    )?.toLocaleString("ru-RU") || "0"}
                  </td>
                </tr>
                <tr className="grand-total">
                  <td colSpan="7">ИТОГО</td>
                  <td>
                    {(
                      commercialData.total_without_nds ?? commercialData.total
                    )?.toLocaleString("ru-RU") || "0"}
                  </td>
                </tr>
                {/* НДС */}
                {commercialData.nds_percent > 0 && (
                  <>
                    <tr className="nds-row">
                      <td colSpan="7">НДС ({commercialData.nds_percent}%)</td>
                      <td>
                        {commercialData.nds_sum?.toLocaleString("ru-RU") || "0"}
                      </td>
                    </tr>
                    <tr className="grand-total-with-nds">
                      <td colSpan="7">ИТОГО с НДС</td>
                      <td>
                        {commercialData.total_with_nds?.toLocaleString(
                          "ru-RU",
                        ) || "0"}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          ) : (
            <div className="no-data">Нет данных для отображения</div>
          )}
        </div>

        {/* Таблица графика платежей */}
        <div
          className={`payment-table ${
            isEditingPaymentSchedule ? "editing" : ""
          }`}
        >
          <div className="payment-table-header">
            <div>
              <h3>График платежей</h3>
              {isEditingPaymentSchedule && (
                <div className="total-amount-info">
                  <span className="total-amount-label">
                    Общая сумма для расчета:{" "}
                  </span>
                  <span className="total-amount-value">
                    {getTotalAmount().toLocaleString("ru-RU")} тенге
                  </span>
                </div>
              )}
            </div>
            {!isEditingPaymentSchedule && (
              <button
                className="edit-payment-schedule-btn"
                onClick={handleStartEditPaymentSchedule}
                title="Редактировать график платежей"
                disabled={!effectiveIsEditable}
                style={{
                  cursor: !effectiveIsEditable ? "not-allowed" : "pointer",
                  opacity: !effectiveIsEditable ? 0.6 : 1,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 2L14 4L5 13H2V10L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Редактировать
              </button>
            )}
          </div>
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Наименование работ</th>
                <th>В процентном соотношении</th>
                <th>Стоимость, тг</th>
                <th>Срок платежа</th>
                {isEditingPaymentSchedule && <th>Действия</th>}
              </tr>
            </thead>
            <tbody>
              {isEditingPaymentSchedule ? (
                <>
                  {tempPaymentSchedule.map((payment, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={payment.work_name}
                          onChange={(e) =>
                            updatePaymentItem(
                              index,
                              "work_name",
                              e.target.value,
                            )
                          }
                          className="payment-input"
                          placeholder="Наименование работ"
                          disabled={!effectiveIsEditable}
                        />
                      </td>
                      <td className="percentage-cell">
                        <input
                          type="number"
                          value={payment.percentage}
                          onChange={(e) =>
                            handleNumberFieldInput(index, "percentage", e)
                          }
                          onFocus={handleNumberFieldFocus}
                          className={`payment-input ${
                            calculatingFields.has(`${index}-percentage`)
                              ? "calculating"
                              : ""
                          }`}
                          placeholder="0"
                          min="0"
                          max="100"
                          title="При изменении процента сумма пересчитается автоматически"
                          disabled={!effectiveIsEditable}
                        />
                        <span className="percentage-symbol">%</span>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={payment.price}
                          onChange={(e) =>
                            handleNumberFieldInput(index, "price", e)
                          }
                          onFocus={handleNumberFieldFocus}
                          className={`payment-input ${
                            calculatingFields.has(`${index}-price`)
                              ? "calculating"
                              : ""
                          }`}
                          placeholder="0"
                          min="0"
                          title="При изменении суммы процент пересчитается автоматически"
                          disabled={!effectiveIsEditable}
                        />
                      </td>
                      <td>
                        <textarea
                          value={payment.deadline_description}
                          onChange={(e) =>
                            updatePaymentItem(
                              index,
                              "deadline_description",
                              e.target.value,
                            )
                          }
                          className="payment-textarea"
                          placeholder="Описание срока платежа"
                          rows="2"
                          disabled={!effectiveIsEditable}
                        />
                      </td>
                      <td className="actions-cell">
                        <button
                          className="remove-payment-btn"
                          onClick={() => removePaymentItem(index)}
                          title="Удалить"
                          disabled={!effectiveIsEditable}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M3 6H5H13"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 6V4M8 4H6M8 4H10"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 6V14C5 14.5523 5.44772 15 6 15H10C10.5523 15 11 14.5523 11 14V6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="add-element-row">
                    <td colSpan={isEditingPaymentSchedule ? "6" : "5"}>
                      <button
                        className="add-payment-btn"
                        onClick={addPaymentItem}
                        disabled={!effectiveIsEditable}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M8 2V14M2 8H14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Добавить элемент
                      </button>
                    </td>
                  </tr>
                  {tempPaymentSchedule.length > 0 && (
                    <tr className="grand-total">
                      <td colSpan="3">ИТОГО</td>
                      <td>
                        {tempPaymentSchedule
                          .reduce(
                            (sum, payment) => sum + (payment.price || 0),
                            0,
                          )
                          .toLocaleString("ru-RU")}
                      </td>
                      <td colSpan={isEditingPaymentSchedule ? "2" : "1"}></td>
                    </tr>
                  )}
                </>
              ) : commercialData?.cp_payment_schedule?.length > 0 ? (
                <>
                  {commercialData.cp_payment_schedule.map((payment, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{payment.work_name}</td>
                      <td>{payment.percentage}%</td>
                      <td>{payment.price?.toLocaleString("ru-RU") || "0"}</td>
                      <td>{payment.deadline_description}</td>
                    </tr>
                  ))}
                  <tr className="grand-total">
                    <td colSpan="3">ИТОГО</td>
                    <td>
                      {commercialData.cp_payment_schedule
                        .reduce((sum, payment) => sum + (payment.price || 0), 0)
                        .toLocaleString("ru-RU")}
                    </td>
                    <td></td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    Нет данных о графике платежей
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Кнопки сохранения/отмены для графика платежей */}
          {isEditingPaymentSchedule && (
            <div className="edit-actions">
              <button
                className="save-btn"
                onClick={handleSavePaymentSchedule}
                disabled={!effectiveIsEditable || isSavingPaymentSchedule}
              >
                {isSavingPaymentSchedule ? (
                  "Сохранение..."
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13 2L6 9L3 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Сохранить график платежей
                  </>
                )}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancelEditPaymentSchedule}
                disabled={!effectiveIsEditable || isSavingPaymentSchedule}
              >
                Отмена
              </button>
            </div>
          )}
        </div>

        {/* Дополнительная информация (редактируемые блоки до реквизитов) */}
        <div className="additional-info">
          <div className="additional-info-editable-block">
            {isEditingAdditionalInfo ? (
              <>
                <div className="info-section">
                  <label className="edit-label">Заголовок:</label>
                  <input
                    type="text"
                    value={designSectionsTitle}
                    onChange={(e) => setDesignSectionsTitle(e.target.value)}
                    className="edit-input edit-input-block"
                    placeholder="Разделы проектирования РП:"
                  />
                  <ol className="editable-list">
                    {designSectionsItems.map((item, idx) => (
                      <li key={idx} className="editable-list-item">
                        <textarea
                          value={item}
                          onChange={(e) => {
                            const next = [...designSectionsItems];
                            next[idx] = e.target.value;
                            setDesignSectionsItems(next);
                          }}
                          className="edit-textarea-list"
                          rows={2}
                        />
                        <button
                          type="button"
                          className="list-item-remove-btn"
                          onClick={() =>
                            setDesignSectionsItems((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          title="Удалить пункт"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M2 4H14M5 4V2H11V4M6 7V11M10 7V11M3 4L4 14H12L13 4"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Удалить
                        </button>
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    className="list-add-btn"
                    onClick={() =>
                      setDesignSectionsItems((prev) => [...prev, ""])
                    }
                  >
                    + Добавить пункт
                  </button>
                </div>

                <div className="info-section">
                  <label className="edit-label">Заголовок:</label>
                  <input
                    type="text"
                    value={excludedCostsTitle}
                    onChange={(e) => setExcludedCostsTitle(e.target.value)}
                    className="edit-input edit-input-block"
                    placeholder="В настоящем ценовом предложении не включены затраты на:"
                  />
                  <ol className="editable-list">
                    {excludedCostsItems.map((item, idx) => (
                      <li key={idx} className="editable-list-item">
                        <textarea
                          value={item}
                          onChange={(e) => {
                            const next = [...excludedCostsItems];
                            next[idx] = e.target.value;
                            setExcludedCostsItems(next);
                          }}
                          className="edit-textarea-list"
                          rows={2}
                        />
                        <button
                          type="button"
                          className="list-item-remove-btn"
                          onClick={() =>
                            setExcludedCostsItems((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          title="Удалить пункт"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M2 4H14M5 4V2H11V4M6 7V11M10 7V11M3 4L4 14H12L13 4"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Удалить
                        </button>
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    className="list-add-btn"
                    onClick={() =>
                      setExcludedCostsItems((prev) => [...prev, ""])
                    }
                  >
                    + Добавить пункт
                  </button>
                </div>

                <div className="info-section">
                  <label className="edit-label">Сроки:</label>
                  <input
                    type="text"
                    value={deadlinesText}
                    onChange={(e) => setDeadlinesText(e.target.value)}
                    className="edit-input edit-input-block"
                    placeholder="Сроки согласно утвержденному графику проектирования."
                  />
                </div>

                <div className="info-section">
                  <label className="edit-label">Исполнитель:</label>
                  <input
                    type="text"
                    value={performerName}
                    onChange={(e) => setPerformerName(e.target.value)}
                    className="edit-input"
                    placeholder="Ф.И.О."
                    style={{ maxWidth: 280 }}
                  />
                </div>
                <div className="info-section">
                  <label className="edit-label">Дата:</label>
                  <input
                    type="text"
                    value={signatureDate}
                    onChange={(e) => setSignatureDate(e.target.value)}
                    className="edit-input"
                    placeholder="ДД.ММ.ГГГГ"
                    style={{ width: 120 }}
                  />
                </div>

                <div className="edit-actions">
                  <button
                    type="button"
                    onClick={handleSaveAdditionalInfo}
                    disabled={isSavingAdditionalInfo}
                    className="save-btn"
                  >
                    {isSavingAdditionalInfo ? "Сохранение…" : "Сохранить"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEditAdditionalInfo}
                    disabled={isSavingAdditionalInfo}
                    className="cancel-btn"
                  >
                    Отмена
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="info-section info-section-with-edit">
                  <div className="display-field display-field-inline">
                    <h4>{designSectionsTitle}</h4>
                    {effectiveIsEditable && (
                      <button
                        type="button"
                        className="mode-btn edit-additional-btn"
                        onClick={() => setIsEditingAdditionalInfo(true)}
                        title="Редактировать блок"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M12 2L14 4L5 13H2V10L12 2Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <ol>
                    {designSectionsItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ol>
                </div>

                <div className="info-section">
                  <h4>{excludedCostsTitle}</h4>
                  <ol>
                    {excludedCostsItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ol>
                </div>

                <div className="info-section">
                  <p>{deadlinesText}</p>
                </div>
              </>
            )}
          </div>

          <div className="info-section">
            <h4>
              Реквизиты «{getCounterpartyLabel(selectedDesignerCounterparty)}»:
            </h4>
            <p>
              {formatCounterpartyAddress(selectedDesignerCounterparty)}
              <br />
              БИН/ИИН {selectedDesignerCounterparty?.bin_or_inn}, ИИК
              KZ226017111000001238, БИК HSBKKZKX, АО Народный банк Казахстана
              <br />
              тел.: {selectedDesignerCounterparty?.phone}
            </p>
          </div>
        </div>

        {/* Подписи (Исполнитель и дата подтягиваются из редактируемого блока выше) */}
        <div className="signatures">
          <div className="signature-block">
            <div className="signature-line" />
            {showDirectorTitle && <div className="signature-position">Директор</div>}
            <div className="signature-name">{selectedDesignerDirectorName}</div>
          </div>
          <div className="performer">
            <div>Исполнитель - {performerName}</div>
          </div>
          <div className="date">от {signatureDate}</div>
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="commercial-offer-actions">
        <button
          className="btn-send-approval"
          onClick={() => setShowApprovalModal(true)}
          disabled={
            !effectiveIsEditable || isSubmittingApproval || !commercialData
          }
        >
          {isSubmittingApproval ? "Отправка..." : "Отправить на согласование"}
        </button>
        {/* <button className="btn-save" disabled={!effectiveIsEditable}>
          Сохранить
        </button> */}
        {/* <button className="btn-print-pdf">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M14 2H2V14H14V2Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6H10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 9H10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 12H8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Печать PDF
        </button> */}
      </div>

      {/* История версий */}
      <VersionHistoryDrawer
        open={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        projectId={projectId}
        type="commercial"
      />

      {/* Модальное окно согласования */}
      <CommercialOfferApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        projectData={projectData}
        isSubmitting={isSubmittingApproval}
      />

      {/* Модальные окна создания контрагентов */}
      <AddCounterpartyModal
        open={showAddDesignerModal}
        onClose={() => setShowAddDesignerModal(false)}
        defaultType="organization"
        onCounterpartyCreated={(newCounterparty) => {
          const newOption = {
            value: normalizeId(newCounterparty.id),
            label: getCounterpartyLabel(newCounterparty),
          };
          setDesignersCounterparties((prev) => [...prev, newCounterparty]);
          setSelectedDesigner(newOption);
          if (selectedRecipient?.value) {
            saveDesignerAndRecipient();
          }
        }}
      />

      <AddCounterpartyModal
        open={showAddRecipientModal}
        onClose={() => setShowAddRecipientModal(false)}
        defaultType="counterparty"
        onCounterpartyCreated={(newCounterparty) => {
          const newOption = {
            value: normalizeId(newCounterparty.id),
            label: getCounterpartyLabel(newCounterparty),
          };
          setRecipientsCounterparties((prev) => [...prev, newCounterparty]);
          setSelectedRecipient(newOption);
          if (selectedDesigner?.value) {
            saveDesignerAndRecipient();
          }
        }}
      />
    </div>
  );
};

export default CommercialOffer;
